<!--
  @description Multi-line text input with inline validation, optional label, and live border
  feedback. Use for long-form text fields such as challenge descriptions or feedback
  messages — wherever a single-line `Input` would be too restrictive.

  Shares the same validation contract as `Input` (required, length, regex), and emits the
  same `(value, status)` tuple on every keystroke so parent forms can stay in sync without
  re-running rules. Validation is deferred until after the first user interaction to avoid
  showing errors on an untouched field.

  @example
  <TextArea
    id="challenge-description"
    title="Description"
    :value="form.description"
    :rows="6"
    :validation="{ required: true, length: { min: 20, max: 500 } }"
    @update:modelValue="(val, status) => { form.description = val; descStatus = status }"
  />

  @accessibility The `<label>` is linked to the `<textarea>` via matching `id` / `for`
  attributes. Error messages appear in a `<p>` directly below the field. Ensure `id` is
  unique per page to avoid duplicate-id violations.
-->
<template>
  <div class="textarea">
    <label v-if="title !== undefined" :for="id" class="textarea__label primary"
      >{{ title }}<span v-if="!validation.required"> ({{ $t('global.optional') }})</span></label
    >
    <textarea
      :id="id"
      :name="title"
      :placeholder="placeholder"
      :value="value"
      :class="classes"
      :rows="rows"
      :aria-invalid="!status.valid && status.touched ? true : undefined"
      :aria-describedby="!status.valid ? `${id}-error` : undefined"
      class="textarea__field"
      @input="updateValue"
    />
    <p v-if="!status.valid" :id="`${id}-error`" role="alert" class="textarea__error">
      {{ status.message }}
    </p>
  </div>
</template>

<script setup lang="ts">
import type { Status, ValidationParams } from '@/types/Types'
import { validate, required, length, regex } from '@/Validation/validation'

import { computed, ref, watchEffect } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const props = defineProps({
  /** Unique HTML id — wired to the `<textarea>` element and matched by the label's `for`. */
  id: {
    type: String,
    required: true
  },
  /** Label text rendered above the textarea. Omit to render without a label. */
  title: {
    type: String,
    required: false,
    default: undefined
  },
  placeholder: {
    type: String,
    required: false,
    default: undefined
  },
  /** Controlled current value. Synced via `watchEffect` so external changes propagate in. */
  value: {
    type: String,
    required: false,
    default: ''
  },
  /** Number of visible text rows. Defaults to 4; increase for longer content like descriptions. */
  rows: {
    type: Number,
    required: false,
    default: 4
  },
  /**
   * Validation rules applied on every keystroke after the first user interaction.
   * - `required` — rejects empty values
   * - `length` — enforces min/max character count
   * - `regex` — tests the value against a custom pattern with a caller-supplied error message
   */
  validation: {
    type: Object as () => ValidationParams,
    required: false,
    default: () => ({
      required: false,
      length: null,
      regex: null
    })
  }
})

const emit = defineEmits<{
  (event: 'update:modelValue', value: string, status: Status): void
}>()

function updateValue(e: Event) {
  const value = (e.target as HTMLInputElement).value
  validateInput(value)
  emit('update:modelValue', value, status.value)
}

const classes = computed(() => {
  return {
    'border-danger': !status.value.valid && status.value.touched,
    'border-success': status.value.valid && status.value.touched
  }
})

const status = ref({ valid: true, message: '', touched: false } as Status)

function validateInput(value: string) {
  const rules = []
  if (props.validation.required) rules.push(required)
  if (props.validation.length) rules.push(length(props.validation.length, t))
  if (props.validation.regex)
    rules.push(
      regex({ regex: props.validation.regex.value, message: props.validation.regex.message }, t)
    )
  status.value = validate(value, rules, t)
}

const inputValue = ref(props.value)

watchEffect(() => {
  inputValue.value = props.value
})

const isFirstRender = ref(true)

watchEffect(() => {
  if (isFirstRender.value) {
    isFirstRender.value = false
    return
  }
  validateInput(inputValue.value)
})
</script>

<style scoped lang="scss">
.textarea {
  display: flex;
  flex-direction: column;
  margin: 1rem 0.5rem;

  &__label {
    margin-bottom: 0.5rem;
    font-weight: bold;
    color: var(--text);
  }

  &__field {
    padding: $base-spacing;
    border: 1px solid var(--input-background);
    border-radius: $xl-border-radius;
    font-size: 1rem;
    min-height: 80px;
    max-height: 300px;
    background-color: var(--input-background);
    color: var(--text);
    font-family: $font-family;
  }

  &__error {
    margin-top: 0.2rem;
    font-size: 0.8rem;
    color: var(--danger);
  }
}
</style>
